import { lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { MarketingLayout, Analytics } from '@/components';
import { PageLoadingFallback } from '@/components/Loading';

// Eager-load the home page for optimal LCP
import { HomePage } from '@/pages';

// Lazy-load core pages
const FeaturesPage = lazy(() =>
  import('@/pages/FeaturesPage').then((m) => ({ default: m.FeaturesPage }))
);
const PricingPage = lazy(() =>
  import('@/pages/PricingPage').then((m) => ({ default: m.PricingPage }))
);
const DemoPage = lazy(() =>
  import('@/pages/DemoPage').then((m) => ({ default: m.DemoPage }))
);
const AboutPage = lazy(() =>
  import('@/pages/AboutPage').then((m) => ({ default: m.AboutPage }))
);
const ContactPage = lazy(() =>
  import('@/pages/ContactPage').then((m) => ({ default: m.ContactPage }))
);
const ResourcesPage = lazy(() =>
  import('@/pages/ResourcesPage').then((m) => ({ default: m.ResourcesPage }))
);

// Lazy-load blog pages
const BlogPage = lazy(() =>
  import('@/pages/BlogPage').then((m) => ({ default: m.BlogPage }))
);
const BlogPostPage = lazy(() =>
  import('@/pages/BlogPostPage').then((m) => ({ default: m.BlogPostPage }))
);
const CaseStudyPage = lazy(() =>
  import('@/pages/CaseStudyPage').then((m) => ({ default: m.CaseStudyPage }))
);

// Lazy-load legal pages
const PrivacyPage = lazy(() =>
  import('@/pages/PrivacyPage').then((m) => ({ default: m.PrivacyPage }))
);
const TermsPage = lazy(() =>
  import('@/pages/TermsPage').then((m) => ({ default: m.TermsPage }))
);
const NotFoundPage = lazy(() =>
  import('@/pages/NotFoundPage').then((m) => ({ default: m.NotFoundPage }))
);

// Lazy-load industry pages as a group (shared chunk via magic comment)
const HVACPage = lazy(() =>
  import(/* webpackChunkName: "industries" */ '@/pages/industries/HVACPage').then((m) => ({
    default: m.HVACPage,
  }))
);
const PlumbingPage = lazy(() =>
  import(/* webpackChunkName: "industries" */ '@/pages/industries/PlumbingPage').then((m) => ({
    default: m.PlumbingPage,
  }))
);
const ElectricalPage = lazy(() =>
  import(/* webpackChunkName: "industries" */ '@/pages/industries/ElectricalPage').then((m) => ({
    default: m.ElectricalPage,
  }))
);
const GarageDoorPage = lazy(() =>
  import(/* webpackChunkName: "industries" */ '@/pages/industries/GarageDoorPage').then((m) => ({
    default: m.GarageDoorPage,
  }))
);
const LandscapingPage = lazy(() =>
  import(/* webpackChunkName: "industries" */ '@/pages/industries/LandscapingPage').then((m) => ({
    default: m.LandscapingPage,
  }))
);
const PestControlPage = lazy(() =>
  import(/* webpackChunkName: "industries" */ '@/pages/industries/PestControlPage').then((m) => ({
    default: m.PestControlPage,
  }))
);
const RoofingPage = lazy(() =>
  import(/* webpackChunkName: "industries" */ '@/pages/industries/RoofingPage').then((m) => ({
    default: m.RoofingPage,
  }))
);
const CleaningPage = lazy(() =>
  import(/* webpackChunkName: "industries" */ '@/pages/industries/CleaningPage').then((m) => ({
    default: m.CleaningPage,
  }))
);
const PoolSpaPage = lazy(() =>
  import(/* webpackChunkName: "industries" */ '@/pages/industries/PoolSpaPage').then((m) => ({
    default: m.PoolSpaPage,
  }))
);
const ApplianceRepairPage = lazy(() =>
  import(/* webpackChunkName: "industries" */ '@/pages/industries/ApplianceRepairPage').then(
    (m) => ({ default: m.ApplianceRepairPage })
  )
);
const LawnCarePage = lazy(() =>
  import(/* webpackChunkName: "industries" */ '@/pages/industries/LawnCarePage').then((m) => ({
    default: m.LawnCarePage,
  }))
);
const TreeServicePage = lazy(() =>
  import(/* webpackChunkName: "industries" */ '@/pages/industries/TreeServicePage').then((m) => ({
    default: m.TreeServicePage,
  }))
);
const WindowCleaningPage = lazy(() =>
  import(/* webpackChunkName: "industries" */ '@/pages/industries/WindowCleaningPage').then((m) => ({
    default: m.WindowCleaningPage,
  }))
);
const PressureWashingPage = lazy(() =>
  import(/* webpackChunkName: "industries" */ '@/pages/industries/PressureWashingPage').then((m) => ({
    default: m.PressureWashingPage,
  }))
);
const PaintingPage = lazy(() =>
  import(/* webpackChunkName: "industries" */ '@/pages/industries/PaintingPage').then((m) => ({
    default: m.PaintingPage,
  }))
);
const HandymanPage = lazy(() =>
  import(/* webpackChunkName: "industries" */ '@/pages/industries/HandymanPage').then((m) => ({
    default: m.HandymanPage,
  }))
);

// Lazy-load competitor comparison pages
const VsServiceTitanPage = lazy(() =>
  import(/* webpackChunkName: "compare" */ '@/pages/compare/VsServiceTitanPage').then((m) => ({
    default: m.VsServiceTitanPage,
  }))
);
const VsJobberPage = lazy(() =>
  import(/* webpackChunkName: "compare" */ '@/pages/compare/VsJobberPage').then((m) => ({
    default: m.VsJobberPage,
  }))
);
const VsHousecallProPage = lazy(() =>
  import(/* webpackChunkName: "compare" */ '@/pages/compare/VsHousecallProPage').then((m) => ({
    default: m.VsHousecallProPage,
  }))
);
const VsFieldEdgePage = lazy(() =>
  import(/* webpackChunkName: "compare" */ '@/pages/compare/VsFieldEdgePage').then((m) => ({
    default: m.VsFieldEdgePage,
  }))
);
const VsServiceFusionPage = lazy(() =>
  import(/* webpackChunkName: "compare" */ '@/pages/compare/VsServiceFusionPage').then((m) => ({
    default: m.VsServiceFusionPage,
  }))
);
const VsWorkizPage = lazy(() =>
  import(/* webpackChunkName: "compare" */ '@/pages/compare/VsWorkizPage').then((m) => ({
    default: m.VsWorkizPage,
  }))
);
const VsSimproPage = lazy(() =>
  import(/* webpackChunkName: "compare" */ '@/pages/compare/VsSimproPage').then((m) => ({
    default: m.VsSimproPage,
  }))
);
const VsFieldPulsePage = lazy(() =>
  import(/* webpackChunkName: "compare" */ '@/pages/compare/VsFieldPulsePage').then((m) => ({
    default: m.VsFieldPulsePage,
  }))
);
const VsGorilladeskPage = lazy(() =>
  import(/* webpackChunkName: "compare" */ '@/pages/compare/VsGorilladeskPage').then((m) => ({
    default: m.VsGorilladeskPage,
  }))
);
const VsServiceM8Page = lazy(() =>
  import(/* webpackChunkName: "compare" */ '@/pages/compare/VsServiceM8Page').then((m) => ({
    default: m.VsServiceM8Page,
  }))
);
const VsKickservPage = lazy(() =>
  import(/* webpackChunkName: "compare" */ '@/pages/compare/VsKickservPage').then((m) => ({
    default: m.VsKickservPage,
  }))
);
const VsMhelpdeskPage = lazy(() =>
  import(/* webpackChunkName: "compare" */ '@/pages/compare/VsMhelpdeskPage').then((m) => ({
    default: m.VsMhelpdeskPage,
  }))
);
const ComparePage = lazy(() =>
  import(/* webpackChunkName: "compare" */ '@/pages/compare/ComparePage').then((m) => ({
    default: m.ComparePage,
  }))
);

export function App() {
  return (
    <BrowserRouter>
      <Analytics />
      <Suspense fallback={<PageLoadingFallback />}>
        <Routes>
          <Route element={<MarketingLayout />}>
            {/* Core Pages */}
            <Route index element={<HomePage />} />
            <Route path="features" element={<FeaturesPage />} />
            <Route path="pricing" element={<PricingPage />} />
            <Route path="demo" element={<DemoPage />} />
            <Route path="about" element={<AboutPage />} />
            <Route path="contact" element={<ContactPage />} />
            <Route path="resources" element={<ResourcesPage />} />
            <Route path="blog" element={<BlogPage />} />
            <Route path="blog/:slug" element={<BlogPostPage />} />
            <Route path="case-study/:slug" element={<CaseStudyPage />} />
            <Route path="privacy" element={<PrivacyPage />} />
            <Route path="terms" element={<TermsPage />} />

            {/* Industry Pages */}
            <Route path="industries/hvac" element={<HVACPage />} />
            <Route path="industries/plumbing" element={<PlumbingPage />} />
            <Route path="industries/electrical" element={<ElectricalPage />} />
            <Route path="industries/garage-doors" element={<GarageDoorPage />} />
            <Route path="industries/landscaping" element={<LandscapingPage />} />
            <Route path="industries/pest-control" element={<PestControlPage />} />
            <Route path="industries/roofing" element={<RoofingPage />} />
            <Route path="industries/carpet-cleaning" element={<CleaningPage />} />
            <Route path="industries/pool-service" element={<PoolSpaPage />} />
            <Route path="industries/appliance-repair" element={<ApplianceRepairPage />} />
            <Route path="industries/lawn-care" element={<LawnCarePage />} />
            <Route path="industries/tree-service" element={<TreeServicePage />} />
            <Route path="industries/window-cleaning" element={<WindowCleaningPage />} />
            <Route path="industries/pressure-washing" element={<PressureWashingPage />} />
            <Route path="industries/painting" element={<PaintingPage />} />
            <Route path="industries/handyman" element={<HandymanPage />} />

            {/* Competitor Comparison Pages */}
            <Route path="compare" element={<ComparePage />} />
            <Route path="vs/servicetitan" element={<VsServiceTitanPage />} />
            <Route path="vs/jobber" element={<VsJobberPage />} />
            <Route path="vs/housecall-pro" element={<VsHousecallProPage />} />
            <Route path="vs/fieldedge" element={<VsFieldEdgePage />} />
            <Route path="vs/service-fusion" element={<VsServiceFusionPage />} />
            <Route path="vs/workiz" element={<VsWorkizPage />} />
            <Route path="vs/simpro" element={<VsSimproPage />} />
            <Route path="vs/fieldpulse" element={<VsFieldPulsePage />} />
            <Route path="vs/gorilladesk" element={<VsGorilladeskPage />} />
            <Route path="vs/servicem8" element={<VsServiceM8Page />} />
            <Route path="vs/kickserv" element={<VsKickservPage />} />
            <Route path="vs/mhelpdesk" element={<VsMhelpdeskPage />} />

            {/* 404 Not Found */}
            <Route path="*" element={<NotFoundPage />} />
          </Route>
        </Routes>
      </Suspense>
    </BrowserRouter>
  );
}
